import { updateAddressMutation } from '@/api/addresses/addressMutations';
import { countriesQuery } from '@/api/countries/countryQueries';
import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import type { Country } from '@/domain/Country';
import type { UserAddress } from '@/domain/User';
import { useFormMutation } from '@/hooks/useFormMutation';
import { updateAddressSchema } from '@/validation/addressValidator';
import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import type * as z from 'zod';
import { toFormikValidate } from 'zod-formik-adapter';

type AddressFormValues = z.infer<typeof updateAddressSchema>;

interface UpdateAddressFormDialogProps {
  address: UserAddress;
  trigger?: React.ReactNode;
}

export function UpdateAddressFormDialog({
  address,
  trigger,
}: UpdateAddressFormDialogProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: countriesData } = useQuery(countriesQuery);
  const updateAddress = useFormMutation(updateAddressMutation(address.id));

  const initialValues = useMemo<AddressFormValues>(() => {
    return {
      name: address.name,
      line1: address.line1,
      city: address.city,
      country: address.country.country_code || '',
      zipcode: address.zipcode,
      line2: address.line2 ?? '',
    };
  }, [address]);

  const formik = useFormik<AddressFormValues>({
    initialValues,
    validate: toFormikValidate(updateAddressSchema),
    enableReinitialize: true,
    onSubmit: async (values) => {
      const countryName = countriesData?.data.find(
        (c: Country) => c.country_code === values.country,
      )?.country;

      await updateAddress.mutateAsync({
        ...values,
        country: countryName || values.country,
      });
      setIsDialogOpen(false);
    },
  });

  const countryOptions = countriesData?.data
    ? countriesData.data.map((country) => ({
        label: country.country,
        value: country.country_code,
      }))
    : [];

  return (
    <>
      {trigger && (
        <div onClick={() => setIsDialogOpen(true)} className="cursor-pointer">
          {trigger}
        </div>
      )}
      <DialogWrapper
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title="Change address"
        maxWidth="md"
      >
        <form onSubmit={formik.handleSubmit} className="space-y-4">
          <Input
            id="line1"
            label="Address Line"
            type="text"
            {...formik.getFieldProps('line1')}
            error={
              formik.touched.line1 && formik.errors.line1
                ? formik.errors.line1
                : undefined
            }
            touched={!!formik.touched.line1}
          />
          <Input
            id="zipcode"
            label="Zip/Postal Code"
            type="text"
            {...formik.getFieldProps('zipcode')}
            error={
              formik.touched.zipcode && formik.errors.zipcode
                ? formik.errors.zipcode
                : undefined
            }
            touched={!!formik.touched.zipcode}
          />
          <Input
            id="city"
            label="City"
            type="text"
            {...formik.getFieldProps('city')}
            error={
              formik.touched.city && formik.errors.city
                ? formik.errors.city
                : undefined
            }
            touched={!!formik.touched.city}
          />
          <Select
            id="country"
            label="Country"
            name="country"
            value={formik.values.country}
            onChange={(val: string) => formik.setFieldValue('country', val)}
            onBlur={formik.handleBlur}
            options={countryOptions}
            error={
              formik.touched.country && formik.errors.country
                ? formik.errors.country
                : undefined
            }
            touched={!!formik.touched.country}
          />
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={updateAddress.isPending}
          >
            {updateAddress.isPending ? 'Changing...' : 'Change address'}
          </Button>
        </form>
      </DialogWrapper>
    </>
  );
}
